import { ClerkLoaded, ClerkLoading, ClerkProvider, Show } from '@clerk/nextjs';
import { getClerkLocalization } from '@firmable/i18n';
import { GoogleTagManager } from '@next/third-parties/google';
import { HydrationBoundary, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Analytics } from '@vercel/analytics/react';
import { ReactQueryDevtools } from '@web/components/ReactQueryDevtools/ReactQueryDevtools';
import { Configs } from '@web/context/Configs';
import { Register } from '@web/context/Context';
import { Features } from '@web/context/Features';
import { FetchHubspotToken } from '@web/context/FetchHubspotToken';
import { Filters as ContextFilters } from '@web/context/Filters';
import { LocaleProvider } from '@web/context/LocaleProvider';
import { useLocaleStore } from '@web/store/App/useLocaleStore';
import { SyncActiveUserOrg } from '@web/context/SyncActiveUserOrg';
import { FilterStoresRehydrator } from '@web/store/Search/Rehydrator';
import { SyncDefaultCountries } from '@web/context/SyncDefaultCountries';
import { SyncParagonSession } from '@web/context/SyncParagonSession';
import { SyncParagonUserToken } from '@web/context/SyncParagonUserToken';
import type { NextPage } from 'next';
import type { AppProps } from 'next/app';
import { NuqsAdapter } from 'nuqs/adapters/next/pages';
import React, { ReactElement, ReactNode } from 'react';
import '../public/globals.css';
import '@web/externals/axios';

import { CheckAuthorizationWithCustomPermissions } from '@clerk/types';

import { Logo } from '@firmable/icons';
import { Interceptor } from '@web/components/Interceptor';
import { SignalsInterceptor } from '@web/components/Interceptor/Signals/Signals';
import { Notification } from '@web/components/Notification';
import { HasValidSubscription } from '@web/components/Plan/HasValidSubscription';
import { PostHog } from '@web/components/PostHog';
import { Telemetry } from '@web/components/Telemetry';
import { ProductFruits } from '@web/components/ProductFruits';
import { RenderIfFeatureAvailable } from '@web/components/RenderIfFeatureAvailable';
import { SubscriptionGuard } from '@web/components/SubscriptionGuard/SubscriptionGuard';
import { Routes } from '@web/constants/routes';
import { installChunkLoadRecovery } from '@web/utility/chunkLoadRecovery';
import { enableMapSet } from 'immer';
import Head from 'next/head';
import Script from 'next/script';

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

enableMapSet();

const queryClient = new QueryClient();

installChunkLoadRecovery();

// Clerk otherwise hot-loads the floating `clerk-js@6` entry, whose lazy chunks are
// version-pinned; when Clerk publishes, sessions straddling the switch fail to load
// them. Bump these alongside @clerk/nextjs.
const CLERK_JS_VERSION = '6.36.0';
const CLERK_UI_VERSION = '1.38.0';

// useLayoutEffect will be available only on the Client.
// If it is not browser, we will use useEffect instead.
if (!process.browser) {
  React.useLayoutEffect = React.useEffect;
}

const publicPath = [`${Routes.signInPath}/[[...index]]`, `${Routes.signUpPath}/[[...index]]`, Routes.maintenance];

function MyApp({ Component, pageProps, router }: AppPropsWithLayout) {
  const locale = useLocaleStore((s) => s.locale);
  const localization = React.useMemo(() => getClerkLocalization(locale), [locale]);

  const condition = React.useCallback((has: CheckAuthorizationWithCustomPermissions) => {
    return (
      has({ role: 'admin' }) ||
      has({ role: 'org:admin' }) ||
      has({ role: 'basic_member' }) ||
      has({ role: 'org:basic_member' })
    );
  }, []);

  const afterSignOutUrl = `/sign-in?action=sign-out`;
  const getLayout = Component.getLayout ?? ((page: ReactElement) => page);
  // SubscriptionGuard leaves plan-selection for /dashboard once the subscription is valid, so the
  // shell prefetchers would only fire here to be thrown away and fire again on the dashboard.
  const isPlanSelection = router.pathname === Routes.planSelectionPath;

  return (
    <ClerkProvider
      localization={localization}
      {...pageProps}
      afterSignOutUrl={afterSignOutUrl}
      __internal_clerkJSVersion={CLERK_JS_VERSION}
      __internal_clerkUIVersion={CLERK_UI_VERSION}
      dynamic
    >
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0,user-scalable=0" />
      </Head>
      <Script src="/fprmain.js" strategy="afterInteractive" />
      <Script src="/fpr-prox.js" strategy="afterInteractive" />

      <ClerkLoading>
        <div className="flex justify-center items-center w-screen h-screen bg-surface-light">
          <Logo.FirmableShort width={48} height={48} className={'animate-ping'} />
        </div>
      </ClerkLoading>
      <ClerkLoaded>
        <Script src="/HSWidgetScript.js" />
        <Script
          id="hs-script-loader"
          strategy="afterInteractive"
          src={`//js.hs-scripts.com/${process.env.NEXT_PUBLIC_HUBSPOT_TRACKING_ID}.js`}
        />
        <QueryClientProvider client={queryClient}>
          <LocaleProvider>
          <HydrationBoundary state={pageProps.dehydratedState}>
            <PostHog.Provider>
              <GoogleTagManager gtmId={process.env.NEXT_PUBLIC_GTM_CONTAINER_ID} />

              <div>
                <PostHog.PageView />
                {/* Outside the signed-in gate and ahead of SyncParagonUserToken, so a stale session ends before a new one starts */}
                <SyncParagonSession />
                <Show when="signed-in">
                  <FilterStoresRehydrator />
                  <Interceptor.ClerkTokenInjector />
                  <Telemetry.UserBinding />
                  {/* Intercepts the signal agent related api calls based on the feature enabled */}
                  <SignalsInterceptor />
                  {!isPlanSelection && (
                    <HasValidSubscription>
                      {/* has t&cs been signed */}
                      <SyncDefaultCountries />
                      <Features />
                      <Configs />
                      <Show when={condition}>
                        <ContextFilters />
                        <Register />
                      </Show>
                      <RenderIfFeatureAvailable feature="hubspot-chat-conversation">
                        <FetchHubspotToken />
                      </RenderIfFeatureAvailable>
                      <SyncParagonUserToken />
                    </HasValidSubscription>
                  )}
                  <SyncActiveUserOrg />
                  <ProductFruits />
                </Show>
                <div>
                  {publicPath.includes(router.pathname) ? (
                    <Component {...pageProps} />
                  ) : (
                    <Show when="signed-in">
                      <NuqsAdapter>
                        <SubscriptionGuard>{getLayout(<Component {...pageProps} />)}</SubscriptionGuard>
                      </NuqsAdapter>
                    </Show>
                  )}
                </div>
                <Notification />
                <ReactQueryDevtools />
              </div>
            </PostHog.Provider>
          </HydrationBoundary>
        </LocaleProvider>
        </QueryClientProvider>
        <Analytics />
      </ClerkLoaded>
    </ClerkProvider>
  );
}

export default MyApp;
